import { ComponentPropertyDataType } from './autogenerated-piping';

/** Canonical `ComponentProperty.Name` of the combined Material + ASTM property. Mirrors `Constants.ComponentPropertyNames.materialAstm` on the API. */
export const MATERIAL_ASTM_PROPERTY_NAME: string = 'MaterialAstm';

/** Decomposed form of a `MaterialAstm` property value. */
export interface MaterialAstmValue {
  material: string;
  astm: string;
}

/**
 * Serializes Material and ASTM into the combined `MaterialAstm` property value.
 *
 * **Why this exists**: the API stores both values in a single `ComponentProperty` row whose
 * `Value` is JSON. ComponentProperty rows are matched by exact `Value` string (not JSON-semantic
 * equality), so the output here must be byte-identical to the API's serializer -- meaning key
 * order (`material` then `astm`), no whitespace, and both keys always present even when empty.
 * @param material The material value, e.g. `CS`.
 * @param astm The ASTM value, e.g. `ASTM A105N`.
 * @returns The JSON string to store as the property value.
 */
export function serializeMaterialAstm(material: string | undefined, astm: string | undefined): string {
  const value: MaterialAstmValue = { material: material?.trim() ?? '', astm: astm?.trim() ?? '' };
  return JSON.stringify(value);
}

/**
 * Parses a combined `MaterialAstm` property value back into its two parts.
 * @param value The stored JSON property value.
 * @returns The decomposed value; empty strings when the input is missing or not valid JSON.
 */
export function deserializeMaterialAstm(value: string | undefined): MaterialAstmValue {
  if (!value) {
    return { material: '', astm: '' };
  }

  try {
    const parsed: Partial<MaterialAstmValue> = JSON.parse(value);
    return { material: parsed?.material ?? '', astm: parsed?.astm ?? '' };
  } catch {
    return { material: '', astm: '' };
  }
}

/**
 * Builds the user-facing display text for a Material/ASTM pair.
 * @param material The material value, e.g. `CS`.
 * @param astm The ASTM value, e.g. `ASTM A105N`.
 * @returns e.g. `CS, ASTM A105N`; just the populated side when only one is set; `''` when neither is.
 */
export function formatMaterialAstm(material: string | undefined, astm: string | undefined): string {
  const trimmedMaterial: string = material?.trim() ?? '';
  const trimmedAstm: string = astm?.trim() ?? '';

  if (trimmedMaterial && trimmedAstm) {
    return `${trimmedMaterial}, ${trimmedAstm}`;
  }
  return trimmedMaterial || trimmedAstm;
}

/**
 * Formats a raw `ComponentProperty.Value` for display, decoding structured values.
 * Use for any generic dictionary grid that renders property values without knowing their type.
 * @param value The raw stored property value.
 * @param dataType The property's declared data type.
 * @returns Display text -- decoded for `Structured` values, unchanged otherwise.
 */
export function formatComponentPropertyValue(value: string | undefined, dataType: ComponentPropertyDataType | undefined): string {
  if (dataType !== ComponentPropertyDataType._2) {
    return value ?? '';
  }

  const materialAstm: MaterialAstmValue = deserializeMaterialAstm(value);
  return formatMaterialAstm(materialAstm.material, materialAstm.astm);
}
